import { activePlayers, COURTS, type Court, type PlayerRecord, type SessionState } from './session';

// The team-selection CSV, pinned by session/contracts/p5-reporting.md and docs/reporting.md. One
// pure function over the folded session: no clock, no locale, no I/O. The download mechanics live
// behind a Dom wrapper; this module only ever produces the string.
//
// This file is the entire handover to the humans who pick teams, and they open it in Excel at 9pm.
// That is why the flags are words rather than 1/0, why nothing is quoted or padded (no value can
// contain a comma, so quoting would only be one more thing to get wrong), and why the last row
// ends in a newline like every other one.

const COLUMNS = ['player', 'division', 'big', 'lightbulb', 'score', 'upvotes'] as const;

// Gold at the top, white last (docs/reporting.md). `COURTS` is the PROMOTION order, white first,
// so the report order is its reverse and stays derived from it rather than restated: a court added
// there cannot be forgotten here.
const DIVISIONS: readonly Court[] = [...COURTS].reverse();

// Columns in order: player, division, big, lightbulb, score, upvotes. Rows are every active player,
// gold division first, score descending inside a division, player number ascending on a tie.
//
// `division`, not `court`: the 60-minute tick moves people after the whistle, so the value is the
// assignment the player leaves with and not a place they stood (docs/reporting.md).
export function buildSessionCsv(state: SessionState): string {
  // A copy, because `activePlayers` hands back a fresh array today and sorting a caller's array in
  // place is the kind of thing that stops being true quietly.
  const rows = [...activePlayers(state)].sort(byDivisionThenScore).map(row);
  // The trailing '' is the trailing newline: a file whose last line has no newline is a file the
  // next tool concatenates wrong. An empty session is therefore the header line and nothing else.
  return [COLUMNS.join(','), ...rows, ''].join('\n');
}

// What the file is called on the admin's disk: the session's own name, lowercased with its spaces
// dashed, then the first 8 characters of the session id. The id half is not decoration — two
// sessions called "u19 boys" on the same day are two different files, and an admin downloading the
// second one must not silently overwrite the first. The id is taken as it stands, because it is an
// identifier and not prose.
export function csvFilename(name: string, sessionId: string): string {
  return `${name.toLowerCase().replaceAll(' ', '-')}-${sessionId.slice(0, 8)}.csv`;
}

// A total order, so two devices folding the same stream write byte-identical files. Score is only
// comparable inside a division — points reset on every court change and there is no global ranking
// (docs/reporting.md) — and the player number is the tiebreak because it is the one value no two
// rows can share.
function byDivisionThenScore(left: PlayerRecord, right: PlayerRecord): number {
  const division = DIVISIONS.indexOf(left.court) - DIVISIONS.indexOf(right.court);
  if (division !== 0) return division;
  if (left.score !== right.score) return right.score - left.score;
  return left.number - right.number;
}

function row(player: PlayerRecord): string {
  return [
    player.number,
    player.court,
    yesNo(player.big),
    yesNo(player.lightbulb),
    player.score,
    player.upvotes,
  ].join(',');
}

// Words, not 1/0: the readers are club admins in a spreadsheet, and a truthy column is a column
// somebody misreads at 9pm.
const yesNo = (value: boolean): string => (value ? 'yes' : 'no');
